import { Component, Input, OnChanges } from '@angular/core';

export interface LegalWheelSection {
  title: string;
  // Trusted, author-authored HTML (the legal page's own static copy) — not
  // user input, so binding it via [innerHTML] after sanitizing is safe.
  body: string;
}

// A playful "spin the wheel" way to browse a legal document's sections —
// hover (or tap) a slice and its content pops up center-stage instead of
// scrolling a wall of text. Reused by both the Privacy Policy and Terms &
// Conditions pages, which only differ in how many sections they have and
// what's in them.
@Component({
  selector: 'app-legal-wheel',
  templateUrl: './legal-wheel.component.html',
  styleUrls: ['./legal-wheel.component.scss'],
})
export class LegalWheelComponent implements OnChanges {
  @Input() sections: LegalWheelSection[] = [];
  @Input() logoSrc = 'assets/Icon-196.png';

  // Cycles through the existing design-system accent colors so adjacent
  // slices are always visually distinct regardless of section count.
  private readonly palette = ['#5a20d6', '#7929e0', '#b5680a', '#d1382f', '#157a4f', '#0f7ea8'];

  slices: { path: string; color: string; labelX: number; labelY: number; index: number }[] = [];
  activeIndex: number | null = null;
  wheelRotation = 0;
  spinning = false;

  private readonly cx = 200;
  private readonly cy = 200;
  private readonly r = 190;

  ngOnChanges(): void {
    this.buildSlices();
  }

  private buildSlices(): void {
    const n = this.sections.length;
    if (!n) {
      this.slices = [];
      return;
    }
    const slice = (2 * Math.PI) / n;
    const labelR = this.r * 0.62;
    this.slices = this.sections.map((_, i) => {
      const startAngle = i * slice - Math.PI / 2;
      const endAngle = startAngle + slice;
      const x1 = this.cx + this.r * Math.cos(startAngle);
      const y1 = this.cy + this.r * Math.sin(startAngle);
      const x2 = this.cx + this.r * Math.cos(endAngle);
      const y2 = this.cy + this.r * Math.sin(endAngle);
      const largeArc = slice > Math.PI ? 1 : 0;
      const path = `M${this.cx},${this.cy} L${x1},${y1} A${this.r},${this.r} 0 ${largeArc} 1 ${x2},${y2} Z`;
      const midAngle = startAngle + slice / 2;
      return {
        path,
        color: this.palette[i % this.palette.length],
        labelX: this.cx + labelR * Math.cos(midAngle),
        labelY: this.cy + labelR * Math.sin(midAngle),
        index: i,
      };
    });
  }

  select(index: number): void {
    this.activeIndex = index;
  }

  close(): void {
    this.activeIndex = null;
  }

  get activeSection(): LegalWheelSection | null {
    return this.activeIndex !== null ? this.sections[this.activeIndex] : null;
  }

  // A few full spins plus a random offset, slowed by a long ease-out
  // transition in the stylesheet — purely a tactile engagement flourish,
  // doesn't "land on" or unlock anything.
  spin(): void {
    if (this.spinning) {
      return;
    }
    this.spinning = true;
    const extraSpins = 4 + Math.floor(Math.random() * 3);
    this.wheelRotation += extraSpins * 360 + Math.floor(Math.random() * 360);
    setTimeout(() => (this.spinning = false), 3200);
  }
}
